/**
 * My groups: view memberships, join groups, set primary, leave.
 * Open groups auto-approve; private groups are invite-only (admin invites; users cannot sign up automatically).
 * User can leave a group only if they have no active/scheduled matches in tournaments that use that group.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { groupJoin, groupLeave } from '../api/groupApi';
import { userUpdateProfile } from '../api/userApi';
import { saveUserToAlgolia } from '../services/userAlgoliaService';
import { GROUP_TYPES, groupTypeRequiresMemberId } from '../constants/groups';
import TopNav from '../components/TopNav';
import { useAlert } from '../contexts/AlertContext';
import './SelectApartment.css';

function membershipId(userId, groupId) {
  return `${userId}_${groupId}`;
}

export default function SelectGroup() {
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { showConfirmPromise } = useAlert();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [joinMode, setJoinMode] = useState(false);
  const [joinGroupId, setJoinGroupId] = useState('');
  const [joinMemberId, setJoinMemberId] = useState('');
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    async function load() {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }
      try {
        const [groupsSnap, membersSnap, matchesSnap, tournamentsSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'groups')),
          getDocs(query(collection(db, 'groupMembers'), where('userId', '==', currentUser.uid))),
          getDocs(query(collection(db, 'matches'), where('status', 'in', ['scheduled', 'in-progress']))),
          getDocs(collection(db, 'tournaments')),
          getDocs(collection(db, 'users'))
        ]);
        setGroups(groupsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setMemberships(membersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setMatches(matchesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTournaments(tournamentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError('Could not load groups.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser?.uid]);

  const myGroupIds = memberships.map(m => m.groupId);
  const joinedGroups = memberships.map(m => {
    const g = groups.find(x => x.id === m.groupId);
    return { ...m, groupName: g?.name, groupShortCode: g?.shortCode, groupType: g?.type, group: g };
  });

  /** User can leave group only if they have no scheduled/in-progress match in a tournament that uses this group. */
  const groupIdsWithActiveMatches = useMemo(() => {
    const out = new Set();
    const tournamentById = Object.fromEntries(tournaments.map(t => [t.id, t]));
    matches.forEach(m => {
      if (m.player1Id !== currentUser?.uid && m.player2Id !== currentUser?.uid) return;
      const t = m.tournamentId ? tournamentById[m.tournamentId] : null;
      if (t && Array.isArray(t.groupIds)) t.groupIds.forEach(gid => out.add(gid));
    });
    return out;
  }, [matches, tournaments, currentUser?.uid]);

  const canLeaveGroup = (groupId) => !groupIdsWithActiveMatches.has(groupId);

  /** Join list: public groups (isOpen) or private groups where user's email was invited. */
  const userEmailLower = (currentUser?.email || '').toString().trim().toLowerCase();
  const groupsToJoin = groups.filter(g => {
    if (myGroupIds.includes(g.id)) return false;
    if (g.isOpen) return true;
    const invited = (g.invitedEmails || []).map(e => (e || '').toString().trim().toLowerCase()).filter(Boolean);
    return invited.includes(userEmailLower);
  });
  const hasApproved = (userProfile?.approvedGroupIds && userProfile.approvedGroupIds.length > 0);

  /** Resolve group admin display (name / email) for a group. */
  const getGroupAdminDisplay = (group) => {
    const adminIds = group?.adminIds || [];
    return adminIds.map(uid => {
      const u = users.find(x => x.id === uid);
      return u ? (u.displayName || u.email || uid) : uid;
    }).filter(Boolean).join(', ') || '—';
  };

  async function handleJoin(e) {
    e.preventDefault();
    setError('');
    if (!joinGroupId || !currentUser?.uid) {
      setError('Select a group.');
      return;
    }
    const group = groups.find(g => g.id === joinGroupId);
    const requiresMemberId = group && groupTypeRequiresMemberId(group.type);
    if (requiresMemberId && !joinMemberId) {
      setError('This group type requires a flat number.');
      return;
    }
    setSaving(true);
    try {
      await groupJoin(joinGroupId, currentUser.displayName || currentUser.email || '', {
        memberId: joinMemberId || undefined,
        flatNumber: joinMemberId || undefined,
      });
      const id = membershipId(currentUser.uid, joinGroupId);
      setMemberships(prev => [...prev, { id, userId: currentUser.uid, groupId: joinGroupId, memberId: joinMemberId || null, approved: true, joinedAt: new Date() }]);
      setJoinMode(false);
      setJoinGroupId('');
      setJoinMemberId('');
      if (refreshUserProfile) await refreshUserProfile();
      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', currentUser.uid)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      await saveUserToAlgolia(
        { id: currentUser.uid, displayName: userData.displayName, email: currentUser.email || userData.email, phone: userData.phone },
        approvedGroupIds
      );
    } catch (err) {
      const msg = err?.message || 'Failed to join.';
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(msg);
      setError(isPermission ? "You don't have permission. Sign in again or contact an admin." : msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleSetPrimary(m) {
    if (!currentUser?.uid) return;
    setSaving(true);
    setError('');
    try {
      const group = groups.find(g => g.id === m.groupId);
      await userUpdateProfile(currentUser.uid, {
        groupId: m.groupId,
        groupName: group?.name || '',
        groupShortCode: group?.shortCode || '',
        groupType: group?.type || GROUP_TYPES.CUSTOM,
        groupMemberId: m.memberId || null,
        groupApproved: true,
        ...(group?.type === GROUP_TYPES.APARTMENT && {
          flatNumber: m.memberId || null,
          apartmentId: m.groupId,
          apartmentName: group?.name || '',
          apartmentShortCode: group?.shortCode || '',
          apartmentApproved: true
        })
      });
      if (refreshUserProfile) await refreshUserProfile();
    } catch (err) {
      setError(err?.message || 'Failed to set primary.');
    } finally {
      setSaving(false);
    }
  }

  async function handleLeave(m) {
    if (!currentUser?.uid) return;
    if (!canLeaveGroup(m.groupId)) {
      setError('You cannot leave this group while you have active or scheduled matches in a tournament that uses it. Finish or withdraw from those matches first.');
      return;
    }
    const ok = await showConfirmPromise(`Leave ${m.groupName || m.groupId}?`, { title: 'Leave group' });
    if (!ok) return;
    setSaving(true);
    setError('');
    try {
      await groupLeave(m.groupId);
      setMemberships(prev => prev.filter(x => x.id !== m.id));
      if (userProfile?.groupId === m.groupId || userProfile?.apartmentId === m.groupId) {
        await userUpdateProfile(currentUser.uid, {
          groupId: null,
          groupName: null,
          groupShortCode: null,
          groupType: null,
          groupMemberId: null,
          groupApproved: false,
          flatNumber: null,
          apartmentId: null,
          apartmentName: null,
          apartmentShortCode: null,
          apartmentApproved: false
        });
        if (refreshUserProfile) await refreshUserProfile();
      }
      const membersSnap = await getDocs(query(collection(db, 'groupMembers'), where('userId', '==', currentUser.uid)));
      const approvedGroupIds = membersSnap.docs.filter((d) => d.data().approved === true).map((d) => d.data().groupId);
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      await saveUserToAlgolia(
        { id: currentUser.uid, displayName: userData.displayName, email: currentUser.email || userData.email, phone: userData.phone },
        approvedGroupIds
      );
    } catch (err) {
      setError(err?.message || 'Failed to leave.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading">Loading...</div>;

  const joinGroup = groups.find(g => g.id === joinGroupId);
  const joinRequiresMemberId = joinGroup && groupTypeRequiresMemberId(joinGroup.type);
  const joinMemberIdOptions = joinGroup?.type === GROUP_TYPES.APARTMENT ? (joinGroup.flatNumbers || []) : [];

  return (
    <div className="select-apartment-container">
      <TopNav title="My groups" />
      <div className="select-apartment-card">
        <h1>My groups</h1>
        <p className="select-apartment-subtitle">
          You can be part of multiple groups, teams or apartments. Public groups: anyone can request to join; admins approve. Private groups: invite-only—you see them only if a group admin invited your email; admins manage members and approve. You can leave a group only when you have no active or scheduled matches in a tournament that uses it.
        </p>
        {error && <div className="error-message">{error}</div>}

        {joinedGroups.length > 0 && (
          <div className="my-groups-list" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Your groups</h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {joinedGroups.map(m => {
                const isPrimary = (userProfile?.groupId === m.groupId || userProfile?.apartmentId === m.groupId);
                const approved = m.approved === true;
                const canLeave = canLeaveGroup(m.groupId);
                return (
                  <li key={m.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                      <strong>{m.groupName || m.groupId}</strong>
                      {m.groupShortCode && <span style={{ color: 'var(--muted-foreground)' }}>({m.groupShortCode})</span>}
                      {m.memberId && <span>· {m.memberId}</span>}
                      {approved ? <span className="status-badge status-completed" style={{ fontSize: 12 }}>Approved</span> : <span className="status-badge status-scheduled" style={{ fontSize: 12 }}>Pending</span>}
                      {isPrimary && <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>Primary</span>}
                    </div>
                    {m.group && (
                      <p style={{ margin: '6px 0 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
                        Group admins: {getGroupAdminDisplay(m.group)}
                      </p>
                    )}
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {approved && !isPrimary && (
                        <button type="button" onClick={() => handleSetPrimary(m)} disabled={saving} className="btn-secondary" style={{ padding: '6px 12px', fontSize: 14 }}>Set as primary</button>
                      )}
                      <button type="button" onClick={() => handleLeave(m)} disabled={saving || !canLeave} className="btn-destructive" style={{ padding: '6px 12px', fontSize: 14 }} title={!canLeave ? 'You have active or scheduled matches in a tournament that uses this group' : ''}>Leave</button>
                    </div>
                    {!canLeave && (
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>You cannot leave while you have active or scheduled matches in a tournament that uses this group.</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!joinMode ? (
          <div>
            {groupsToJoin.length === 0 ? (
              joinedGroups.length === 0 && (
                <p className="select-apartment-empty">
                  No groups are set up yet. Create a group from Groups, or ask an admin to add you as a group admin.
                </p>
              )
            ) : (
              <button type="button" onClick={() => setJoinMode(true)} className="btn-primary">Join a group</button>
            )}
            {hasApproved && <button type="button" onClick={() => navigate('/')} className="btn-secondary" style={{ marginLeft: 12 }}>Back to Dashboard</button>}
          </div>
        ) : (
          <form onSubmit={handleJoin}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Join a group</h2>
            <p style={{ fontSize: 13, color: 'var(--muted-foreground)', marginBottom: 12 }}>Only public groups and private groups you were invited to are listed. Private groups do not allow sign-up without an invite.</p>
            <div className="form-group">
              <label>Group *</label>
              <select value={joinGroupId} onChange={(e) => { setJoinGroupId(e.target.value); setJoinMemberId(''); }} required className="select-apartment-input">
                <option value="">Select group</option>
                {groupsToJoin.map(g => (
                  <option key={g.id} value={g.id}>{g.shortCode ? `${g.name} (${g.shortCode})` : g.name} {g.isOpen ? '— Public' : '— Invite only'}</option>
                ))}
              </select>
            </div>
            {joinGroup && joinRequiresMemberId && (
              <div className="form-group">
                <label>Flat number *</label>
                <select value={joinMemberId} onChange={(e) => setJoinMemberId(e.target.value)} required className="select-apartment-input">
                  <option value="">Select flat</option>
                  {joinMemberIdOptions.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            )}
            {joinGroup && !joinRequiresMemberId && (
              <div className="form-group">
                <label>Member ID (optional)</label>
                <input type="text" value={joinMemberId} onChange={(e) => setJoinMemberId(e.target.value)} placeholder="Optional" className="select-apartment-input" />
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Joining…' : 'Join'}</button>
              <button type="button" onClick={() => { setJoinMode(false); setJoinGroupId(''); setJoinMemberId(''); setError(''); }} className="btn-secondary">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
